<template>
    <div :class="parentClass">
        <draggable
            v-model="stages"
            handle=".draggable"
            item-key="id"
            tag="ul"
            class="w-full"
        >
            <template #item="{ element, index }">
                <li class="mb-12">
                    <div :class="[ index ? 'shadow-md' : '' ]"
                         class="rounded h-72 flex items-center gap-16 px-24 border border-primary-20 bg-white"
                    >
                        <progress-icon :stage="element" :size="element.onboarding ? '24' : '20'"></progress-icon>
                        <span class="text-body font-semibold mr-auto">{{ element.name }}</span>
                        <div class="flex items-center gap-8">
                            <div v-if="element.onboarding" class="text-secondary-80 mr-8">
                                <tooltip :content="$t('stages.onboarding_activated')"
                                         delayed
                                         offset="0,12"
                                >
                                    <svg-icon size="24" type="location-home"></svg-icon>
                                </tooltip>
                            </div>
                            <simple-button
                                look="icon"
                                display="primary"
                                icon="edit"
                                small
                                @click="edit(element)"
                            />
                            <simple-button
                                v-if="index"
                                look="icon"
                                display="danger"
                                icon="delete"
                                small
                                @click="confirmDelete(element)"
                            />
                            <span v-if="index" class="draggable cursor-pointer ml-8">
                                <svg-icon class="max-w-16 max-h-16" type="drag-indicator"></svg-icon>
                            </span>
                        </div>
                    </div>
                </li>
            </template>
        </draggable>
        <div>
            <button
                class="py-16 rounded border border-primary-20 bg-primary-5 w-full flex items-center justify-center group text-primary-60 hover:text-primary-80"
                @click="create"
            >
                <span class="flex items-center gap-8">
                    <span class="p-4 rounded-32 bg-primary-60 group-hover:bg-primary-80 text-primary-5">
                        <svg-icon class="w-18 h-18" type="add" />
                    </span>
                    <span class="text-body font-semibold first-letter:uppercase -mb-px">
                        {{ $t('jobs.pipeline_editor.add_stage') }}
                    </span>
                </span>
            </button>
        </div>

        <template v-if="!onlyDragAndDrop">
            <div class="flex justify-end pt-32">
                <checkbox v-model="saveAsDefault">
                    {{ $t('jobs.pipeline_editor.save_as_default') }}
                </checkbox>
            </div>

            <div class="p-16 rounded border border-primary-20 bg-primary-5 mt-48 mb-24">
                <competence-templates
                    class="mb-16"
                    v-model="selectedCompetenceTemplate"
                    ref="competence-templates"
                >
                    <simple-button
                        icon="add-circle"
                        icon-position="left"
                        @click="addingNewCompetenceTemplate = true"
                    >
                        {{ $t('settings.add_new_template') }}
                    </simple-button>
                </competence-templates>

                <interview-templates
                    v-model="selectedInterviewTemplate"
                    ref="interview-templates"
                >
                    <simple-button
                        icon="add-circle"
                        icon-position="left"
                        @click="addingNewInterviewTemplate = true"
                    >
                        {{ $t('settings.add_new_template') }}
                    </simple-button>
                </interview-templates>
            </div>
            <div>
                <div class="flex items-center justify-end">
                    <simple-button
                        id="save-and-close"
                        :loading="savingDefault"
                        class="mr-8"
                        icon="save"
                        @click="save(['close', false])"
                    >{{ $t('jobs.save_and_close') }}
                    </simple-button>
                    <simple-button
                        id="save-and-next"
                        :loading="savingDefault"
                        look="primary"
                        display="secondary"
                        icon="save"
                        @click="save(['next'])"
                    >{{ $t('jobs.save_and_next') }}
                    </simple-button>
                </div>
            </div>
        </template>

        <edit-stage v-if="editing" :job="job" :stage="editing" @close="stopEdit"></edit-stage>
        <edit-stage v-if="creating" :job="job" @close="stopCreate"></edit-stage>

        <popup-modal-confirm
            v-if="deleting"
            :confirmText="$t('general.delete_subject', {subject: $t('stages.stage')})"
            :message="deletingMessage"
            :show-confirm-button="deleting.applications_count === 0"
            :working="performingDeleteRequest"
            show-exclamation
            @close="cancelDelete"
            @confirm="deleteStage(deleting)"
        />

        <new-module-template-popup
            v-if="addingNewCompetenceTemplate"
            type="competence-templates"
            v-model="activeCompetenceTemplate"
            :service="competenceTemplateService"
            @close="closeCompetenceTemplatePopupHandler"
        />

        <new-module-template-popup
            v-if="addingNewInterviewTemplate"
            type="interview-templates"
            v-model="activeInterviewTemplate"
            :service="interviewTemplateService"
            @close="closeInterviewTemplatePopupHandler"
        />
    </div>
</template>

<script>
import Stage from '../../models/Stage';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import * as stages from '../../services/StageService';
import * as defaultStages from '../../services/DefaultStageService';
import * as jobService from "../../services/JobService";
import * as competenceTemplateService from "../../services/CompetenceTemplateService";
import * as interviewTemplateService from "../../services/InterviewTemplateService";

import draggable from 'vuedraggable';
import SvgIcon from './SvgIcon.vue';
import RoundButton from './RoundButton.vue';
import SimpleButton from './SimpleButton.vue';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import EditStage from './EditStage.vue';
import Tooltip from '../navigation/Tooltip.vue';
import Checkbox from '../forms/Checkbox.vue';
import ProgressIcon from './ProgressIcon.vue';
import SelectDropdown from "../forms/SelectDropdown.vue";
import PopupModal from "./PopupModal.vue";
import CompetenceTemplates from "./CompetenceTemplates.vue";
import InterviewTemplates from "./InterviewTemplates.vue";
import NewModuleTemplatePopup from "../NewModuleTemplatePopup";
import {mapStores} from "pinia";
import {useNotificationsStore} from "../../state/modules/notifications";
import {useRepo} from "pinia-orm";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

export default {
    components: {
        'svg-icon': SvgIcon,
        'simple-button': SimpleButton,
        'round-button': RoundButton,
        'popup-modal-confirm': PopupModalConfirm,
        'draggable': draggable,
        'edit-stage': EditStage,
        'tooltip': Tooltip,
        'checkbox': Checkbox,
        'progress-icon': ProgressIcon,
        SelectDropdown,
        PopupModal,
        CompetenceTemplates,
        InterviewTemplates,
        NewModuleTemplatePopup
    },
    props: {
        job: Object,
        onlyDragAndDrop: Boolean,
        overrideParentClass: String,
    },
    data: function () {
        return {
            editing: null,
            creating: false,
            deleting: null,
            performingDeleteRequest: false,
            saveAsDefault: false,
            savingDefault: false,
            competences: null,
            addingNewCompetenceTemplate: false,
            addingNewInterviewTemplate: false,
            selectedCompetenceTemplate: this.job.competence_template_id,
            selectedInterviewTemplate: this.job.interview_template_id,
            activeCompetenceTemplate: null,
            activeInterviewTemplate: null,
        };
    },
    computed: {
        ...mapStores(useNotificationsStore),
        stages: {
            get() {
                return useRepo(Stage)
                    .where('job_id', this.job.id)
                    .orderBy('order')
                    .get();
            },
            set(stages) {
                this.resort(stages);
            },
        },
        deletingMessage() {
            if (this.deleting.applications_count > 0) {
                if (this.deleting.applications_count > 1) {
                    return this.$t('jobs.create.max_amount_candidates', {amount: this.deleting.applications_count});
                } else {
                    return this.$t('jobs.create.max_amount_candidate', {amount: this.deleting.applications_count});
                }
            } else {
                return this.$t('general.confirm_delete_subject', {subject: this.$t('stages.stage')});
            }
        },
        parentClass() {
            if (this.overrideParentClass) {
                return this.overrideParentClass;
            }

            return 'bg-white shadow mb-64 rounded p-32 max-w-960 mx-auto';
        },
    },
    methods: {
        create() {
            this.creating = true;
        },
        stopCreate() {
            this.creating = false;
        },
        edit(stage) {
            this.editing = stage;
        },
        stopEdit() {
            this.editing = null;
        },
        confirmDelete(stage) {
            this.deleting = stage;
        },
        cancelDelete() {
            this.deleting = null;
        },
        deleteStage(stage) {
            this.performingDeleteRequest = true;

            stages.destroy(stage.id).then(() => {
                useRepo(Stage).destroy(stage.id);
            }).then(() => {
                const data = useRepo(Stage)
                    .where('job_id', this.job.id)
                    .get()
                    .map((item) => {
                        const order = item.order > stage.order ? item.order - 1 : item.order;
                        const siblingCount = item.sibling_count - 1;
                        return {
                            id: item.id,
                            order: order,
                            sibling_count: siblingCount
                        }
                    });
                useRepo(Stage).save(data);

                this.deleting = null;
                this.performingDeleteRequest = false;
            });
        },
        resort(sortedStages) {
            let sort = sortedStages.map((stage, index) => ({id: stage.id, order: index}));

            let original = useRepo(Stage).whereIn('id', sort.map(sort => sort.id)).get().map(stage => ({
                id: stage.id,
                order: stage.order,
            }));

            useRepo(Stage).save(sort);

            return stages.updateSort(this.job.id, sort)
                .then(result => {
                    this.notificationsModuleStore[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('general.order_single_changed', {subject: this.$t('jobs.pipeline')}),
                    });
                })
                .catch(error => {
                    useRepo(Stage).save(original);
                });
        },
        async save(todo) {
            if (this.saveAsDefault) {
                this.savingDefault = true;
                await this.saveDefaultStages();

                this.savingDefault = false;
            }
            await this.saveJob();
            this.$emit(...todo);
        },
        saveJob() {
            jobService.update(this.job.id, {
                data: {
                    attributes: {
                        competence_template_id: this.selectedCompetenceTemplate,
                        interview_template_id: this.selectedInterviewTemplate,
                    }
                },
            })
        },
        saveDefaultStages() {
            return defaultStages.index().then(result => {

                let requests = [];
                result.data.filter(stage => stage.attributes.order > 0)
                    .forEach(stage => {
                        requests.push(defaultStages.destroy(stage.id));
                    });

                requests.push(defaultStages.update(
                    result.data.find(stage => stage.attributes.order == 0).id,
                    {
                        data: {
                            attributes: {
                                name: this.stages.find(stage => stage.order == 0).name,
                            },
                        },
                    },
                ));

                this.stages.filter(stage => stage.order > 0).forEach(stage => {
                    requests.push(defaultStages.store({
                        data: {
                            attributes: {
                                name: stage.name,
                                onboarding: stage.onboarding,
                                order: stage.order,
                            },
                        },
                    }));
                });

                return Promise.all(requests);
            });
        },
        closeCompetenceTemplatePopupHandler(confirm = false) {
            this.addingNewCompetenceTemplate = false;
            this.selectedCompetenceTemplate = !confirm ? this.job.competence_template_id : this.activeCompetenceTemplate.id;
            this.activeCompetenceTemplate = null;
        },
        closeInterviewTemplatePopupHandler(confirm = false) {
            this.addingNewInterviewTemplate = false;
            this.selectedInterviewTemplate = !confirm ? this.job.interview_template_id : this.activeInterviewTemplate.id;
            this.activeInterviewTemplate = null;
        },
        competenceTemplateService() {
            return competenceTemplateService;
        },
        interviewTemplateService() {
            return interviewTemplateService;
        }
    },
    mounted() {
        if (this.$route.query.scroll) {
            document.getElementById('content-container').scrollTop = this.$refs[this.$route.query.scroll].$el.offsetTop;
        }
    }
};
</script>
